import { z } from "zod";
import { sport } from "./sports";
import { outcomeType, winCriteria } from "./outcome";

// =========================================================
// ENUMS
// =========================================================

const EventTypeEnum = z.enum(["MATCH", "SEASON", "TOURNAMENT", "RACE"]);

const SelectionResultEnum = z.enum(["WIN", "LOSE", "VOID", "UNDECIDED"]);

const MarketStatusEnum = z.enum([
  "ACTIVE",
  "SUSPENDED",
  "DEACTIVATED",
  "SETTLED",
  "CANCELLED",
  "UNAVAILABLE",
  "LIVE",
  "WAITING",
  "OPEN",
  "HANDED_OVER",
  "INVALID",
]);

const MarketBettingTypeEnum = z.enum(["FIXED_ODDS", "PARIMUTUEL"]);

const SelectionTypeEnum = z.enum([
  "SingleOutcome",
  "SameEventMulti",
  "Exotic",
  "Quaddie",
]);

const StakeSourceEnum = z.enum(["AVAILABLE", "PROMOTION_USER"]);

// =========================================================
// SHARED OBJECTS
// =========================================================

// Base Competitor - Polymorphic
const FranchiseCompetitorSchema = z.object({
  type: z.literal("FRANCHISE"),
  id: z.uuid(),
  name: z.string(),
  abbreviation: z.string(),
  iconUri: z.string().nullish(),
  nationality: z.string().nullish(),
});

const PlayerCompetitorSchema = z.object({
  type: z.literal("PLAYER"),
  id: z.uuid(),
  name: z.string(),
  nickname: z.string(),
  nationality: z.string().nullish(),
  iconUri: z.string().nullish(),
  abbreviation: z.string().nullish(),
});

const RaceCompetitorSchema = z.object({
  id: z.uuid(),
  raceId: z.uuid(),
  name: z.string(),
  number: z.number().int(),
  status: z.string().nullish(),
  startingPosition: z.string().nullish(),
  hcpDraw: z.string().nullish(),
  finishPosition: z.number().int().nullish(),
  jockey: z.string().nullish(),
  trainer: z.string().nullish(),
  scratched: z.boolean(),
  scratchedAt: z.iso.datetime().nullish(),
  emergency: z.boolean().nullish(),
  formAttributes: z.record(z.string(), z.string()),
  weight: z.string().nullish(),
  age: z.string().nullish(),
  sex: z.string().nullish(),
});

const BaseCompetitorSchema = z
  .discriminatedUnion("type", [
    FranchiseCompetitorSchema,
    PlayerCompetitorSchema,
  ])
  .or(RaceCompetitorSchema); // RaceCompetitor doesn't have 'type' field

// Betting Event Reference
const BettingEventReferenceViewSchema = z.object({
  sport: sport,
  eventType: EventTypeEnum,
  eventId: z.uuid(),
  id: z.uuid(),
  eventName: z.string().nullish(),
  displayName: z.string().nullish(),
  scheduledStartTime: z.iso.datetime().nullish(),
  attributes: z.record(z.string(), z.string()).nullish(),
  competitors: z.record(z.uuid(), BaseCompetitorSchema).nullish(),
  streamUri: z.string().nullish(),
  activeMarketCount: z.number().int().nullish(),
  hub: z.string().nullish(),
});

// Same Event Multi Outcome
const SameEventMultiOutcomeViewSchema = z.object({
  outcomeId: z.uuid(),
  outcomeResult: SelectionResultEnum,
  outcomeName: z.string(),
  outcomeType: outcomeType,
  marketId: z.uuid(),
  marketName: z.string(),
  marketType: z.string(),
  marketStatus: MarketStatusEnum,
  attributes: z.record(z.string(), z.string()).nullish(),
  competitor: BaseCompetitorSchema.nullish(),
  winCriteria: winCriteria.nullish(),
});

// Betting Market (for Quaddie)
const BettingMarketSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    id: z.uuid(),
    name: z.string(),
    marketType: z.union([z.string(), z.any()]), // Could be enum or object
    event: BettingEventReferenceViewSchema,
    specifiers: z.record(z.string(), z.string()).nullish(),
    status: MarketStatusEnum,
    favourite: z.boolean(),
    groups: z.array(z.string()),
    nextBetStop: z.iso.datetime().nullish(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    bettingType: z.string().nullish(), // Need clarification on: MarketBettingTypeEnum,
    promotional: z.boolean(),
    mainMarket: z.boolean(),
    allowedSelectionTypes: z.array(z.string()),
  }),
);

// =========================================================
// SHARED SELECTION INTERFACE
// =========================================================

const BaseSelectionSchema = z.object({
  type: SelectionTypeEnum,
  legNumber: z.number().int(),
  void: z.boolean(),
  isVoid: z.boolean().optional(),
  voidReason: z.string().nullish(),
  result: SelectionResultEnum,
  event: BettingEventReferenceViewSchema,
  deadHeatFactor: z.number().nullish(),
  correlationId: z.uuid().nullish(),
  tag: z.string().nullish(), //EventProductFeeTagTypeEnum.nullish(),
  exoticCombinations: z.number().int().nullish(),
  marketNextBetStop: z.iso.datetime().nullish(),
});

// =========================================================
// SELECTION TYPES
// =========================================================

// 1. Standard Single Bet
const OutcomeSelectionViewSchema = BaseSelectionSchema.extend({
  type: z.literal("SingleOutcome"),
  odds: z.number(),
  settlementOdds: z.number(),
  deductions: z.number().nullish(),
  outcomeId: z.uuid(),
  outcomeName: z.string(),
  outcomeType: outcomeType,
  marketId: z.uuid(),
  marketName: z.string(),
  marketStatus: MarketStatusEnum,
  marketBettingType: z.string().nullish(), // Need clarification on: MarketBettingTypeEnum,
  attributes: z.record(z.string(), z.string()).nullish(),
  competitorId: z.uuid().nullish(),
  competitor: BaseCompetitorSchema.nullish(),
});

// 2. Same Event Multi
const SameEventMultiSelectionViewSchema = BaseSelectionSchema.extend({
  type: z.literal("SameEventMulti"),
  odds: z.number(),
  settlementOdds: z.number(),
  deductions: z.number().nullish(),
  outcomes: z.record(z.uuid(), SameEventMultiOutcomeViewSchema),
});

// 3. Exotic Multi
const ExoticMultiSelectionViewSchema = BaseSelectionSchema.extend({
  type: z.literal("Exotic"),
  odds: z.number(),
  settlementOdds: z.number(),
  deductions: z.number().nullish(),
  stakeSource: StakeSourceEnum,
  outcomes: z.record(z.uuid(), SameEventMultiOutcomeViewSchema),
});

// 4. Quaddie
const QuaddieSelectionViewSchema = BaseSelectionSchema.extend({
  type: z.literal("Quaddie"),
  deadHeatFactor: z.number().nullish(),
  odds: z.number(),
  outcomes: z.record(z.uuid(), SameEventMultiOutcomeViewSchema),
  market: BettingMarketSchema,
});

// =========================================================
// DISCRIMINATED UNION
// =========================================================

const SelectionViewSchema = z.discriminatedUnion("type", [
  OutcomeSelectionViewSchema,
  SameEventMultiSelectionViewSchema,
  ExoticMultiSelectionViewSchema,
  QuaddieSelectionViewSchema,
]);

// =========================================================
// ROOT SCHEMA
// =========================================================

const SelectionsMapSchema = z.record(z.string(), SelectionViewSchema);

const SelectionsArraySchema = z.array(SelectionViewSchema);

// =========================================================
// EXPORT TYPES
// =========================================================

export type EventType = z.infer<typeof EventTypeEnum>;
export type SelectionResult = z.infer<typeof SelectionResultEnum>;
export type MarketStatus = z.infer<typeof MarketStatusEnum>;
export type MarketBettingType = z.infer<typeof MarketBettingTypeEnum>;
export type SelectionType = z.infer<typeof SelectionTypeEnum>;
export type StakeSource = z.infer<typeof StakeSourceEnum>;
export type FranchiseCompetitor = z.infer<typeof FranchiseCompetitorSchema>;
export type PlayerCompetitor = z.infer<typeof PlayerCompetitorSchema>;
export type RaceCompetitor = z.infer<typeof RaceCompetitorSchema>;
export type BaseCompetitor = z.infer<typeof BaseCompetitorSchema>;

export type BettingEventReferenceView = z.infer<
  typeof BettingEventReferenceViewSchema
>;
export type SameEventMultiOutcomeView = z.infer<
  typeof SameEventMultiOutcomeViewSchema
>;
export type BettingMarket = z.infer<typeof BettingMarketSchema>;

export type OutcomeSelectionView = z.infer<typeof OutcomeSelectionViewSchema>;
export type SameEventMultiSelectionView = z.infer<
  typeof SameEventMultiSelectionViewSchema
>;
export type ExoticMultiSelectionView = z.infer<
  typeof ExoticMultiSelectionViewSchema
>;
export type QuaddieSelectionView = z.infer<typeof QuaddieSelectionViewSchema>;

export type SelectionView = z.infer<typeof SelectionViewSchema>;
export type SelectionsMap = z.infer<typeof SelectionsMapSchema>;
export type SelectionsArray = z.infer<typeof SelectionsArraySchema>;

// =========================================================
// EXPORT SCHEMAS
// =========================================================

export {
  EventTypeEnum,
  SelectionResultEnum,
  MarketStatusEnum,
  MarketBettingTypeEnum,
  SelectionTypeEnum,
  StakeSourceEnum,
  FranchiseCompetitorSchema,
  PlayerCompetitorSchema,
  RaceCompetitorSchema,
  BaseCompetitorSchema,
  BettingEventReferenceViewSchema,
  SameEventMultiOutcomeViewSchema,
  BettingMarketSchema,
  OutcomeSelectionViewSchema,
  SameEventMultiSelectionViewSchema,
  ExoticMultiSelectionViewSchema,
  QuaddieSelectionViewSchema,
  SelectionViewSchema,
  SelectionsMapSchema,
  SelectionsArraySchema,
};
